import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ServicePaymentStatus } from '../types';
import { fetchPaymentsBySaleOrder } from '../services';

interface CorporateCardPaymentsContextValue {
	servicePayments: ServicePaymentStatus[];
	loading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
	getPaymentsForService: (serviceId: string) => ServicePaymentStatus | null;
}

const CorporateCardPaymentsContext = createContext<CorporateCardPaymentsContextValue | null>(null);

interface CorporateCardPaymentsProviderProps {
	saleOrderId: string;
	children: React.ReactNode;
}

export const CorporateCardPaymentsProvider = ({
	saleOrderId,
	children,
}: CorporateCardPaymentsProviderProps): JSX.Element => {
	const [servicePayments, setServicePayments] = useState<ServicePaymentStatus[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(async (): Promise<void> => {
		if (!saleOrderId) {
			setServicePayments([]);
			return;
		}
		try {
			setLoading(true);
			setError(null);
			const res = await fetchPaymentsBySaleOrder(saleOrderId);
			setServicePayments(res.services);
		} catch {
			setError('No se pudieron cargar los pagos con tarjeta corporativa.');
			setServicePayments([]);
		} finally {
			setLoading(false);
		}
	}, [saleOrderId]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const getPaymentsForService = useCallback(
		(serviceId: string): ServicePaymentStatus | null => servicePayments.find((s) => s.serviceId === serviceId) ?? null,
		[servicePayments],
	);

	const value = useMemo(
		() => ({ servicePayments, loading, error, refresh, getPaymentsForService }),
		[servicePayments, loading, error, refresh, getPaymentsForService],
	);

	return <CorporateCardPaymentsContext.Provider value={value}>{children}</CorporateCardPaymentsContext.Provider>;
};

export const useCorporateCardPayments = (): CorporateCardPaymentsContextValue => {
	const context = useContext(CorporateCardPaymentsContext);
	if (!context) {
		throw new Error('useCorporateCardPayments debe usarse dentro de CorporateCardPaymentsProvider');
	}
	return context;
};
